import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Users, Calendar, MapPin, Trash2, ExternalLink } from "lucide-react";
import { EVENT_TYPE_LABELS } from "@/lib/eventTypes";

export default function Dashboard() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/events");
      setEvents(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este evento? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/events/${id}`);
      setEvents((prev) => prev.filter((e) => e.id !== id));
      toast.success("Evento eliminado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-14">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <span className="text-xs uppercase tracking-[0.24em] text-coral">Panel</span>
            <h1 className="mt-3 font-heading text-4xl md:text-5xl text-ink-900 font-light">
              Hola, {user?.name?.split(" ")[0] || "amig@"}
            </h1>
            <p className="mt-2 text-ink-500">Aquí están tus hojas de inscripción.</p>
          </div>
          <Button
            className="rounded-full bg-coral hover:bg-coral-hover text-white px-6 h-11"
            onClick={() => nav("/crear")}
            data-testid="dashboard-create-btn"
          >
            <Plus size={18} strokeWidth={1.75} className="mr-2" />
            Nuevo evento
          </Button>
        </div>

        {loading ? (
          <p className="text-ink-500">Cargando…</p>
        ) : events.length === 0 ? (
          <div className="border border-dashed border-line rounded-3xl p-16 text-center bg-white">
            <h3 className="font-heading text-2xl text-ink-900">Todavía no tienes eventos</h3>
            <p className="mt-3 text-ink-500">Empieza con una plantilla y compártela en segundos.</p>
            <Button
              className="mt-6 rounded-full bg-coral hover:bg-coral-hover text-white px-6"
              onClick={() => nav("/crear")}
              data-testid="dashboard-empty-create-btn"
            >
              Crear mi primer evento
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((e, i) => {
              const isCal = e.event_type === "calendar";
              const isPot = e.event_type === "potluck" || !e.event_type;
              let total = 0, filled = 0, filledLabel = "registros";
              if (isCal) {
                total = e.calendar_slots?.length || 0;
                filled = e.calendar_slots?.filter((s) => s.booked)?.length || 0;
                filledLabel = "reservados";
              } else if (isPot) {
                total = e.slots.reduce((a, s) => a + s.quantity, 0);
                filled = e.slots.reduce((a, s) => a + s.filled, 0);
                filledLabel = "apuntados";
              } else {
                total = e.submissions_count || 0;
                filled = e.submissions_count || 0;
                filledLabel = "registros";
              }
              return (
                <div
                  key={e.id}
                  className="bg-white border border-line rounded-2xl p-6 shadow-sm transition-transform duration-200 ease-out hover:-translate-y-1 hover:shadow-md animate-fade-up"
                  style={{ animationDelay: `${i * 60}ms` }}
                  data-testid={`event-card-${i}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-heading text-xl text-ink-900 leading-tight">{e.title}</h3>
                      {!isPot && (
                        <span className="mt-1 inline-block text-[10px] uppercase tracking-widest bg-coral/10 text-coral rounded-full px-2 py-0.5">
                          {EVENT_TYPE_LABELS[e.event_type] || e.event_type}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => remove(e.id)}
                      className="text-ink-300 hover:text-coral transition-colors"
                      aria-label="Eliminar"
                      data-testid={`event-delete-${i}`}
                    >
                      <Trash2 size={16} strokeWidth={1.75} />
                    </button>
                  </div>
                  {e.event_date && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-ink-500">
                      <Calendar size={14} strokeWidth={1.75} />
                      {e.event_date}
                    </div>
                  )}
                  {e.location && (
                    <div className="mt-1 flex items-center gap-2 text-sm text-ink-500">
                      <MapPin size={14} strokeWidth={1.75} />
                      {e.location}
                    </div>
                  )}
                  <div className="mt-5 flex items-center justify-between text-sm">
                    <span className="text-ink-500 flex items-center gap-1.5">
                      <Users size={14} strokeWidth={1.75} /> {isPot || isCal ? `${filled} / ${total}` : filled} {filledLabel}
                    </span>
                    {isPot && (
                      <span className="text-xs uppercase tracking-widest text-sage">{e.category}</span>
                    )}
                  </div>
                  <div className="mt-6 flex gap-2">
                    <Link to={`/eventos/${e.id}`} className="flex-1">
                      <Button variant="outline" className="w-full rounded-full border-line hover:bg-cream-100" data-testid={`event-manage-${i}`}>
                        Administrar
                      </Button>
                    </Link>
                    <Link to={`/e/${e.slug}`} target="_blank">
                      <Button variant="ghost" className="rounded-full hover:bg-cream-100" data-testid={`event-open-${i}`}>
                        <ExternalLink size={16} strokeWidth={1.75} />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
